import useHost from './useHost'
import { useEffect, useState } from 'react'
import { List, Site } from '@microsoft/microsoft-graph-types'
import useAxios from '../hooks/useAxios'
import { Destination } from '../context/AxiosContext'
import { Buffer } from 'buffer'
import * as UUID from 'uuid'

export type SharePointProps = {
    sharePoint: SharePoint
    setDriveId: (value) => void

    errorMessage: string
}

export type SharePoint = {
    driveId: string
    siteId: string
    webId: string
    listId: string

    site?: Site
    list?: List
}

export default function useSharePoint(): SharePointProps {
    const axios = useAxios()
    const host = useHost()

    const [driveId, setDriveId] = useState<string>()
    const [sharePoint, setSharePoint] = useState<SharePoint>()
    const [errorMessage, setErrorMessage] = useState<string>()

    useEffect(() => {
        setDriveId(host?.DriveId)
    }, [host])

    useEffect(() => {
        setSharePoint(parseDriveId(driveId))
    }, [driveId])

    function callSharePoint<D>(url: string, setData: (data: D) => void, setErrorMessage: (message: string) => void): Promise<any> {
        return axios
            .get<D>(url, { baseURL: Destination.SharePoint })
            .then(response => {
                setData(response.data)
            })
            .catch(error => {
                setData(undefined)
                setErrorMessage(error.toString())
            })
    }

    useEffect(() => {
        if (sharePoint) {
            setErrorMessage(driveId && undefined)

            // Chain the calls and finally set the entire entity to force changes in the entire Dialog.
            callSharePoint(
                `/sites/${sharePoint.siteId}`,
                data => (sharePoint.site = data),
                message => setErrorMessage(`Get site: ${message}`),
            )
                .then(() =>
                    callSharePoint(
                        `/sites/${sharePoint.siteId}/lists/${sharePoint.listId}`,
                        data => (sharePoint.list = data),
                        message => setErrorMessage(`Get list: ${message}`),
                    ),
                )
                .then(() => setSharePoint({ ...sharePoint }))
        } else {
            setErrorMessage('Er is geen geldige Drive ID ingevuld.')

            setSharePoint({
                ...sharePoint,
                site: undefined,
                list: undefined,
            })
        }
    }, [sharePoint?.siteId, sharePoint?.listId])

    return { sharePoint, setDriveId, errorMessage }
}

const convertGuidBuffer = (bytes: Buffer): Buffer => {
    // Courtesy of https://stackoverflow.com/a/17947129/862825
    return Buffer.from([
        ...bytes.subarray(0, 4).reverse(), // time_low
        ...bytes.subarray(4, 6).reverse(), // time_mid
        ...bytes.subarray(6, 8).reverse(), // time_high
        ...bytes.subarray(8, 10), // clock
        ...bytes.subarray(10, 16), // node
    ])
}

const stringifyGuid = (bytes: Buffer): string => UUID.stringify(convertGuidBuffer(bytes))

const parseGuid = (guid: string): Uint8Array => convertGuidBuffer(UUID.parse(guid.replaceAll(/[^0-9a-fA-F-]/g, '')) as Buffer)

const parseDriveId = (driveId: string): SharePoint => {
    // Courtesy of https://www.techmikael.com/2021/01/microsoft-graph-encoding-and-decoding.html
    const bytes: Buffer = Buffer.from((driveId || '').substring(2).replaceAll('_', '/').replaceAll('-', '+'), 'base64')

    if (bytes.length !== 48) {
        return undefined
    }

    return {
        driveId,
        siteId: stringifyGuid(bytes.subarray(0, 16)),
        webId: stringifyGuid(bytes.subarray(16, 32)),
        listId: stringifyGuid(bytes.subarray(32, 48)),
    }
}

export const formatDriveId = (ids: SharePoint): string => {
    if (!ids.siteId || !ids.webId || !ids.listId) {
        return undefined
    }

    const bytes: Buffer = Buffer.from([
        ...(parseGuid(ids.siteId) as Uint8Array),
        ...(parseGuid(ids.webId) as Uint8Array),
        ...(parseGuid(ids.listId) as Uint8Array),
    ])
    return `b!${bytes.toString('base64').replaceAll('/', '_').replaceAll('+', '-')}`
}
